import { useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/services/supabase'
import { useAuthContext } from '@/components/auth'
import type { InboxMessage } from '@/types'
import type { RealtimeChannel, RealtimePostgresInsertPayload } from '@supabase/supabase-js'

/**
 * useRealtimeNotifications Hook (T513)
 * Subscribes to real-time inbox message inserts via Supabase Realtime.
 *
 * Based on FLOW-04-NOTIFICATIONS.md:
 * - Subscribes to notifications.inbox table INSERT events for current user
 * - Calls onNewNotification callback for toast display
 * - Invalidates notification queries for cache refresh
 */

export interface UseRealtimeNotificationsOptions {
  /** Callback when a new inbox message arrives */
  onNewNotification?: (message: InboxMessage) => void
  /** Whether to enable the subscription (default: true) */
  enabled?: boolean
}

export interface UseRealtimeNotificationsReturn {
  /** Whether the channel is currently connected */
  isConnected: boolean
  /** Any error from the subscription */
  error: Error | null
}

export function useRealtimeNotifications(
  options: UseRealtimeNotificationsOptions = {}
): UseRealtimeNotificationsReturn {
  const { onNewNotification, enabled = true } = options
  const { user } = useAuthContext()
  const queryClient = useQueryClient()

  const channelRef = useRef<RealtimeChannel | null>(null)
  const isConnectedRef = useRef(false)
  const errorRef = useRef<Error | null>(null)

  const handleInsert = useCallback(
    (payload: RealtimePostgresInsertPayload<InboxMessage>) => {
      const message = payload.new as InboxMessage

      onNewNotification?.(message)

      // Invalidate queries to refresh lists and counts
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['grouped-notifications'] })
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
    [onNewNotification, queryClient]
  )

  useEffect(() => {
    if (!enabled || !user?.id) {
      return
    }

    // Create channel subscribing to notifications.inbox INSERT events
    const channel = supabase
      .channel(`inbox:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'notifications',
          table: 'inbox',
          filter: `user_id=eq.${user.id}`,
        },
        handleInsert
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          isConnectedRef.current = true
          errorRef.current = null
        } else if (status === 'CHANNEL_ERROR') {
          isConnectedRef.current = false
          errorRef.current = new Error('Failed to subscribe to inbox channel')
        } else if (status === 'CLOSED') {
          isConnectedRef.current = false
        }
      })

    channelRef.current = channel

    // Cleanup on unmount or when user/enabled changes
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
        isConnectedRef.current = false
      }
    }
  }, [enabled, user?.id, handleInsert])

  return {
    isConnected: isConnectedRef.current,
    error: errorRef.current,
  }
}
